import { useState } from 'react';
import { Sparkles, Copy, Check, Bot, ShieldCheck, Plug } from 'lucide-react';
import Layout from '../components/Layout';
import { Card, PageHeader, Button } from '../components/ui';

const MCP_URL = `${(import.meta.env.VITE_API_URL || 'https://api.myleadsyncer.com').replace(/\/$/, '')}/mcp`;

const TOOLS = [
  'list_connections',
  'create_form', 'update_form', 'publish_form', 'get_form_embed', 'list_forms', 'get_form', 'delete_form',
  'create_quiz', 'update_quiz', 'publish_quiz', 'get_quiz_embed', 'list_quizzes', 'get_quiz', 'delete_quiz',
  'create_button', 'update_button', 'publish_button', 'get_button_embed', 'list_buttons', 'get_button', 'delete_button',
  'get_form_field_reference',
];

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="primary"
      icon={copied ? Check : Copy}
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className="flex-shrink-0"
    >
      {copied ? 'Copied!' : 'Copy'}
    </Button>
  );
}

export default function AiConnector() {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <PageHeader
          eyebrow="Integrations"
          title="AI Connector (MCP)"
          description="Connect Claude, ChatGPT, or any AI assistant to MyLeadSyncer so it can build your forms, quizzes, and buttons — and hand you embed code for any website."
          actions={<Sparkles className="w-6 h-6 text-accent-ink" />}
        />

        {/* Connection URL */}
        <Card padded className="mb-5">
          <label className="ds-label">Your MCP server URL</label>
          <p className="text-xs text-ink-3 mb-3">Add this as a custom connector in your AI tool. You'll sign in with your MyLeadSyncer email and password to authorize it.</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-surface-2 border border-line rounded-[11px] px-3 py-2 text-sm font-mono text-ink break-all">{MCP_URL}</code>
            <CopyButton text={MCP_URL} />
          </div>
        </Card>

        {/* Setup steps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <Card padded>
            <div className="flex items-center gap-2 mb-2"><Bot className="w-4 h-4 text-accent-ink" /><h3 className="font-display text-sm font-semibold text-ink">Claude (claude.ai / Desktop)</h3></div>
            <ol className="text-sm text-ink-2 list-decimal pl-4 space-y-1.5">
              <li>Open <b>Settings → Connectors</b>.</li>
              <li>Click <b>Add custom connector</b>.</li>
              <li>Paste the URL above and continue.</li>
              <li>Sign in with your MyLeadSyncer account and approve.</li>
              <li>Ask Claude to “create a feedback form and give me the embed code.”</li>
            </ol>
          </Card>
          <Card padded>
            <div className="flex items-center gap-2 mb-2"><Bot className="w-4 h-4 text-accent-ink" /><h3 className="font-display text-sm font-semibold text-ink">ChatGPT</h3></div>
            <ol className="text-sm text-ink-2 list-decimal pl-4 space-y-1.5">
              <li>Enable <b>Developer mode / Connectors</b> in settings.</li>
              <li>Add a connector and paste the URL above.</li>
              <li>Authorize with your MyLeadSyncer account.</li>
              <li>Ask it to build a form, quiz, or button for your site.</li>
            </ol>
          </Card>
        </div>

        {/* Prerequisite */}
        <div className="bg-warning-soft border border-line rounded-[15px] p-4 mb-5 flex gap-3">
          <Plug className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
          <div className="text-sm text-ink-2">
            <b className="text-ink">Before you start:</b> connect at least one active ClickFunnels account under{' '}
            <a href="/vault/connections" className="underline font-medium text-accent-ink">Connections</a>. Forms, quizzes, and
            buttons sync leads there. If you have exactly one, the AI selects it automatically.
          </div>
        </div>

        {/* Security */}
        <Card padded className="mb-5">
          <div className="flex items-center gap-2 mb-2"><ShieldCheck className="w-4 h-4 text-success" /><h3 className="font-display text-sm font-semibold text-ink">What it can access</h3></div>
          <p className="text-sm text-ink-2">
            A connected AI can create, edit, publish, and delete <b>your own</b> forms, quizzes, and buttons, and
            read their embed codes. It is scoped to your account only — it can never see or touch another user's
            data. Your ClickFunnels API keys are never exposed to the AI. You can revoke access anytime from your
            AI tool's connector settings.
          </p>
        </Card>

        {/* Tools */}
        <Card padded>
          <h3 className="font-display text-sm font-semibold text-ink mb-2">Available tools ({TOOLS.length})</h3>
          <div className="flex flex-wrap gap-1.5">
            {TOOLS.map((t) => (
              <code key={t} className="text-xs bg-surface-2 text-ink-2 rounded px-2 py-1 font-mono">{t}</code>
            ))}
          </div>
        </Card>
      </div>
    </Layout>
  );
}
